/**
 * <mega-menu> — disclosure controller for the header's mega panels.
 * CSS owns hover-opening on pointer devices; this element owns everything
 * stateful: the chevron disclosure buttons (keyboard + touch), aria-expanded
 * truthfulness on hover, Escape (close + return focus to the button), and
 * outside-click closing. Setting [data-js] turns off the no-JS
 * :focus-within fallback so tabbing doesn't force panels open.
 *
 * It also primes the exploded diagrams. They ship `loading="lazy"` so they
 * cost nothing on page load, but a lazy image inside a panel that is hidden
 * until hover only starts fetching once that panel opens — so the first hover
 * on each category showed the numbered dots on empty white. Flipping every
 * diagram to eager on the first pointer/focus entering the nav row buys the
 * fetch a head start while the cursor is still travelling.
 */
if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      connectedCallback() {
        this.setAttribute('data-js', '');
        this.items = [...this.querySelectorAll('.header__nav-item--mega')];

        this.onClick = (e) => {
          const toggle = e.target.closest('.header__nav-toggle');
          if (!toggle) return;
          const item = toggle.closest('.header__nav-item--mega');
          const open = !item.classList.contains('is-open');
          this.closeAll();
          if (open) {
            item.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
          }
        };

        this.onPointer = () => {
          // Keep aria truthful for CSS hover opens/closes.
          for (const item of this.items) {
            const toggle = item.querySelector('.header__nav-toggle');
            if (!toggle) continue;
            const open = item.classList.contains('is-open') || item.matches(':hover');
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          }
        };

        this.onKeydown = (e) => {
          if (e.key !== 'Escape') return;
          const item = e.target.closest('.header__nav-item--mega');
          if (!item) return;
          const toggle = item.querySelector('.header__nav-toggle');
          this.closeAll();
          if (toggle) toggle.focus(); // focus stays visible on the trigger
        };

        this.onOutside = (e) => {
          if (!this.contains(e.target)) this.closeAll();
        };

        // Pair a numbered row with its dot. Recomputed on every pointerover
        // rather than toggled on enter/leave: moving between the number <span>
        // and the label text would otherwise fire out-then-over and flicker.
        this.onPair = (e) => {
          const panel = e.target.closest?.('.mega__inner');
          if (!panel) return;
          const spot = e.target.closest('[data-spot]');
          const key = spot ? spot.dataset.spot : null;
          for (const el of panel.querySelectorAll('[data-spot]')) {
            el.classList.toggle('is-paired', el.dataset.spot === key);
          }
        };

        this.clearPairs = () => {
          for (const el of this.querySelectorAll('[data-spot].is-paired')) {
            el.classList.remove('is-paired');
          }
        };

        this.primeDiagrams = () => {
          this.removeEventListener('pointerenter', this.primeDiagrams);
          this.removeEventListener('focusin', this.primeDiagrams);
          for (const img of this.querySelectorAll('.mega__diagram-image')) {
            img.loading = 'eager';
          }
        };

        this.addEventListener('click', this.onClick);
        this.addEventListener('mouseover', this.onPointer);
        this.addEventListener('mouseleave', this.onPointer);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('pointerenter', this.primeDiagrams);
        this.addEventListener('focusin', this.primeDiagrams);
        this.addEventListener('pointerover', this.onPair);
        this.addEventListener('focusin', this.onPair);
        this.addEventListener('pointerleave', this.clearPairs);
        this.addEventListener('focusout', this.clearPairs);
        document.addEventListener('click', this.onOutside);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('mouseover', this.onPointer);
        this.removeEventListener('mouseleave', this.onPointer);
        this.removeEventListener('keydown', this.onKeydown);
        this.removeEventListener('pointerenter', this.primeDiagrams);
        this.removeEventListener('focusin', this.primeDiagrams);
        this.removeEventListener('pointerover', this.onPair);
        this.removeEventListener('focusin', this.onPair);
        this.removeEventListener('pointerleave', this.clearPairs);
        this.removeEventListener('focusout', this.clearPairs);
        document.removeEventListener('click', this.onOutside);
      }

      closeAll() {
        for (const item of this.items) {
          item.classList.remove('is-open');
          const toggle = item.querySelector('.header__nav-toggle');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        }
      }
    }
  );
}
